import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { get, size } from "lodash";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Stack,
  Divider,
  Chip,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  AlertTitle,
} from "@mui/material";
import { color } from "../../utils/constant";

import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import { useSelector } from "react-redux";
import useDoctorAvailability from "../../data/talons/DoctorAvailability/useDoctorAvailability";
import { RHFSelect } from "../../components/shared/RHFSelect";
import { useDoctorAvailabilityForm } from "../../data/talons/DoctorAvailability/useDoctorAvailabilityForm";

import { getDoctorHolidayData } from "../../data/selectors/DoctorHoliday/doctorHolidayForm.selector";
import useDoctorHoliday from "../../data/talons/DoctorHoliday/useDoctorHoliday";
import { useDoctorHolidayForm } from "../../data/talons/DoctorHoliday/useDoctorHolidayForm";
import {
  getDoctorList,
  getLeaveList,
} from "../../data/selectors/DoctorHoliday/doctorHoliday.selector";
import { format } from "date-fns";
import MaterialUIPickers from "../../components/shared/DesktopDatePicker";
import { enqueueSnackbar } from "notistack";

export default function DoctorHolidayForm(props) {
  const editItem = useSelector(getDoctorHolidayData);
  const doctorList = useSelector(getDoctorList);
  const leaveList = useSelector(getLeaveList);
  const { fetchDoctorComboList, fetchLeaveComboList } = useDoctorHoliday();

  const {
    loader,
    loading,
    DoctorHolidayEntrySchema,
    handleStatusComboChange,
    DoctorId,
    status,
    DoctorHolidayUpdate,
    DoctorHolidayEntry,
    findDoctorFromLabel,
    findLeaveLabel,
    getDoctorHolidayById,
    LeaveId,
    handleLeaveDateChange,
    appointmentWarning,
    setAppointmentWarning,
  } = useDoctorHolidayForm({
    existingStatus: get(editItem, "status", 0),
    existingDoctorName: get(editItem, "doctor_id", ""),
    existingLeaveName: get(editItem, "leave_type_id", ""),
    existingLeaveDate: get(editItem, "from_date", ""),
  });

  useEffect(() => {
    fetchDoctorComboList();
    fetchLeaveComboList();
  }, []);

  const defaultValues = useMemo(() => ({
    name: get(editItem, "name", ""),
    leave: get(editItem, "leave_type", ""),
  }));

  const HolidayDate = new Date(
    get(editItem, "from_date", format(new Date(), "yyyy-MM-dd"))
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    setValue,
  } = useForm({
    resolver: joiResolver(DoctorHolidayEntrySchema),
    defaultValues,
  });

  const onUserSubmit = useCallback(async () => {
    try {
      const res =
        get(editItem, "id", null) === null
          ? await DoctorHolidayEntry({
            ...getValues(),
          })
          : await DoctorHolidayUpdate({
            ...getValues(),
            id: get(editItem, "id", null),
          });
      if (res === true) {
        props.postSubmit();
      }
    } catch (err) {
      console.log("errr", err);
    }
  }, [DoctorHolidayEntry, DoctorHolidayUpdate]);

  const onlyNumbers = (e) => {
    e.target.value = e.target.value.replace(/[^0-9:]/g, "");

    if (e.target.value.toString().length === 0) return;
    return (e.target.value = e.target.value.slice(0, 10));
  };

  return (
    <React.Fragment>
      <Typography variant="h5" gutterBottom>
        Doctor Holiday Form
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {appointmentWarning && (
        <Alert
          severity="error"
          onClose={() => setAppointmentWarning(null)}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: '1px solid #f44336',
            alignItems: 'flex-start',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
            ⚠️ Cannot Mark {leaveList?.find(l => l.id === LeaveId)?.label || "Holiday"} — Active Appointments Found!
          </AlertTitle>
          {appointmentWarning.message}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MaterialUIPickers
            required
            onChange={(val) => {
              setAppointmentWarning(null);
              handleLeaveDateChange(val);
            }}
            id="from_date"
            label="Holiday Date"
            type="date"
            value={HolidayDate}
            size={"small"}
            sx={{ width: "100%" }}
            InputLabelProps={{
              shrink: true,
            }}
          ></MaterialUIPickers>
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="name"
            label="Doctor Name"
            freeSolo
            data={doctorList || []}
            defaultValue={DoctorId}
            onInputChange={(value) => findDoctorFromLabel(value)}
            id={doctorList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={12}>
          <RHFAutocomplete
            borderRadius={"0px"}
            isRequired={true}
            name="leave"
            label="Leave Type"
            freeSolo
            data={leaveList || []}
            defaultValue={LeaveId}
            onInputChange={(value) => findLeaveLabel(value)}
            id={leaveList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={3} sm={3}>
          <Typography>
            <FormControlLabel
              sx={{ mt: -1 }}
              control={
                <Checkbox
                  sx={{
                    "&.Mui-checked": {
                      color: color.button,
                    },
                  }}
                  id="is_labour"
                  checked={status}
                  onChange={handleStatusComboChange}
                />
              }
              label="Active"
            />
          </Typography>
        </Grid>

        <Grid container direction="row">
          <Grid item xs={12} sm={12}>
            <Stack direction="row" justifyContent="end">
              <Button
                variant="contained"
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  backgroundColor: color.button,
                  "&:hover": {
                    backgroundColor: color.button,
                  },
                }}
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(onUserSubmit)();
                }}
              >
                Save
              </Button>
              <Button
                sx={{
                  height: 40,
                  borderRadius: 3,
                  m: 1,
                  borderColor: color.button,
                  color: color.button,
                  "&:hover": {
                    color: color.button,
                  },
                }}
                variant="outlined"
                onClick={(e) => {
                  e.preventDefault();
                  props.postSubmit();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
